import { Program, Registrations } from '@/app/models/com/registrations.model';
import { DsyListElementConf } from '@/app/models/dsy-drop-down.model';
import { DisplayLabel } from '@/app/models/locale-data.model';
import { ERPWidget } from '@/app/models/widget.model';
import { ERPRegistrationService } from '@/app/services/erp-registration.service';
import { MixPanelEvent, MixPanelService } from '@/app/services/mixpanel.service';
import { EmptyStateComponent } from '@/app/shared/components/empty-state/empty-state.component';
import { WidgetHeaderComponent } from '@/app/shared/components/widget-header/widget-header.component';
import { environment } from '@/environments/environment';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CookieService } from 'ngx-shared-services';

@Component({
  selector: 'dr-flex-home-ui-erp-widget',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [TranslateModule, RouterModule, WidgetHeaderComponent, EmptyStateComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './erp-widget.component.html',
  styleUrl: './erp-widget.component.scss',
})
export class ERPWidgetComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly erpRegistrationService = inject(ERPRegistrationService);
  private readonly mixpanelService = inject(MixPanelService);
  widget = input.required<ERPWidget>();
  private readonly cookieService = inject(CookieService);
  readonly locale = signal<string>(this.normalizeLocale(this.cookieService.getI18NLocale() || environment.defaults.locale));
  readonly isLoading = signal(false);
  readonly errorMessage = signal<string | null>(null);
  private readonly dropdownRefreshKey = signal(0);

  /**
   * Raw backend registrations.
   */
  readonly registrations = signal<Registrations[]>([]);

  /**
   * Selected program_id.
   */
  readonly selectedProgramId = signal<string | null>(null);

  /**
   * Selected registration id.
   */
  readonly selectedRegistrationId = signal<string | null>(null);

  readonly programOptions = computed<Program[]>(() => {
    const programsById = new Map<string, Program>();

    for (const registration of this.registrations()) {
      if (programsById.has(registration.program_id)) {
        continue;
      }

      programsById.set(registration.program_id, {
        id: registration.program_id,
        display_labels: registration.program.display_labels,
      });
    }

    return this.sortProgramsByData(Array.from(programsById.values()));
  });

  readonly programsDropDownListSingle = computed<DsyListElementConf[]>(() => {
    this.dropdownRefreshKey();

    const selectedProgramId = this.selectedProgramId();

    return this.programOptions().map(
      (program) =>
        ({
          id: program.id,
          labelContent: this.getBestLabel(program.display_labels, 'Unnamed program'),
          density: 'wide',

          isSelected: program.id === selectedProgramId,
          onListElementClick: () => this.handleProgramClick(program.id),
        }) as DsyListElementConf
    );
  });

  /**
   * Site dropdown entries
   */
  readonly sitesDropDownListSingle = computed<DsyListElementConf[]>(() => {
    this.dropdownRefreshKey();

    const selectedRegistrationId = this.selectedRegistrationId();

    return this.registrationsForSelectedProgram().map(
      (registration) =>
        ({
          id: registration.id,
          labelContent: this.getBestLabel(registration.display_labels, 'Unnamed site'),
          density: 'wide',
          isSelected: registration.id === selectedRegistrationId,
          onListElementClick: () => this.handleSiteClick(registration.id),
        }) as DsyListElementConf
    );
  });

  /**
   * Registrations for currently selected program.
   */
  readonly registrationsForSelectedProgram = computed<Registrations[]>(() => {
    const selectedProgramId = this.selectedProgramId();

    if (!selectedProgramId) {
      return [];
    }

    return this.sortRegistrationsByData(this.registrations().filter((registration) => registration.program_id === selectedProgramId));
  });

  protected handleProgramClick(programId: string): void {
    if (this.selectedProgramId() === programId) {
      this.dropdownRefreshKey.update((value) => value + 1);
      return;
    }

    // this.selectedProgramId.set('0');
    this.selectedProgramId.set(programId);
    this.selectFirstRegistrationForProgram(programId);
  }

  protected handleSiteClick(registrationId: string): void {
    if (this.selectedRegistrationId() === registrationId) {
      this.dropdownRefreshKey.update((value) => value + 1);
      return;
    }

    this.selectedRegistrationId.set(registrationId);
  }

  constructor() {
    this.fetchRegistrations();
  }

  /**
   * currently selected registration
   */
  readonly selectedRegistration = computed<Registrations | null>(() => {
    const selectedRegistrationId = this.selectedRegistrationId();
    const selctedProgramId = this.selectedProgramId();

    this.mixpanelService.trackEvent(MixPanelEvent.DROPDOWN_ERP, { selectedProgramId: selctedProgramId, selectedRegistrationId: selectedRegistrationId });

    if (!selectedRegistrationId) {
      return null;
    }

    return this.registrations().find((registration) => registration.id === selectedRegistrationId) ?? null;
  });

  readonly selectedSiteName = computed<string>(() => {
    const selectedRegistration = this.selectedRegistration();

    if (!selectedRegistration) {
      return '';
    }

    return this.getBestLabel(selectedRegistration.display_labels, 'selected site');
  });

  readonly energyReductionPlanContent = computed<string>(() => {
    return this.selectedRegistration()?.EMSReg.energy_reduction_plan?.trim() ?? '';
  });

  readonly hasEnergyReductionPlanContent = computed<boolean>(() => {
    return this.energyReductionPlanContent().length > 0;
  });

  private fetchRegistrations(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.erpRegistrationService
      .getRegistrations()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (registrations) => {
          const registrationsWithERP = registrations.filter((registration) => registration.product?.prez_conf?.display_erp_homepage);

          this.registrations.set(registrationsWithERP);
          this.initializeSelection(registrationsWithERP);
          this.isLoading.set(false);
        },
        error: () => {
          this.registrations.set([]);
          this.selectedProgramId.set(null);
          this.selectedRegistrationId.set(null);
          this.errorMessage.set('Unable to load the energy reduction plan.');
          this.isLoading.set(false);
        },
      });
  }

  private initializeSelection(registrations: Registrations[]): void {
    const firstProgramId = this.programOptions()[0]?.id;

    this.selectedProgramId.set(firstProgramId);
    this.selectFirstRegistrationForProgram(firstProgramId, registrations);
  }

  private selectFirstRegistrationForProgram(programId: string | null, sourceRegistrations = this.registrations()): void {
    if (!programId) {
      this.selectedRegistrationId.set(null);
      return;
    }

    const firstRegistration = this.sortRegistrationsByData(sourceRegistrations.filter((r) => r.program_id === programId))[0];

    this.selectedRegistrationId.set(firstRegistration?.id ?? null);
  }

  private getBestLabel(labels: DisplayLabel[] | null | undefined, fallback: string): string {
    if (!labels?.length) {
      return fallback;
    }

    const currentLocale = this.locale();

    return (
      labels.find((label) => this.normalizeLocale(label.locale) === currentLocale)?.label ??
      labels.find((label) => this.normalizeLocale(label.locale) === 'en_US')?.label ??
      labels[0]?.label ??
      fallback
    );
  }

  private normalizeLocale(locale: string): string {
    const [language, country] = locale.replace('-', '_').split('_');

    if (!language || !country) {
      return locale;
    }

    return `${language.toLowerCase()}_${country.toUpperCase()}`;
  }

  private hasProgramData(programId: string): boolean {
    return this.registrations().some((r) => r.program_id === programId && r.EMSReg.energy_reduction_plan?.trim());
  }

  private hasRegistrationData(registration: Registrations): boolean {
    return !!registration.EMSReg.energy_reduction_plan?.trim();
  }

  private sortProgramsByData(programs: Program[]): Program[] {
    return programs.sort(
      this.compareByData(
        (p) => this.hasProgramData(p.id),
        (p) => p.display_labels[0].label
      )
    );
  }

  private sortRegistrationsByData(registrations: Registrations[]): Registrations[] {
    return registrations.sort(
      this.compareByData(
        (r) => this.hasRegistrationData(r),
        (r) => r.display_labels[0].label
      )
    );
  }

  private compareByData<T>(hasData: (item: T) => boolean, getLabel: (item: T) => string) {
    return (a: T, b: T) => {
      const diff = Number(hasData(b)) - Number(hasData(a));
      return diff || getLabel(a).localeCompare(getLabel(b));
    };
  }
}
